import { fg } from '@atlaskit/platform-feature-flags';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { type ActivityClient, type ActivityItem } from '@atlassian/recent-work-client';

import {
	NLPSearchResultFormat,
	type NLPSearchTeamResultType,
	type NLPSearchWhoResultCollaboratorDataType,
	type SearchAIAnswerQueryType,
	type TraceHeaders,
} from '../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { logger } from '../../../common/utils/logger';
import { sanitizeAndMapRecentWorkActivity, trimARI } from '../../util';

import {
	KNOWLEDGE_CARD_TEAM_DATA_OPERATION_NAME,
	KNOWLEDGE_CARD_TEAM_SEARCH_RELEVANCE_OPERATION_NAME,
	KnowledgeCardTeamDataQuery,
	type KnowledgeCardTeamDataType,
	type KnowledgeCardTeamDataVariables,
	type KnowledgeCardTeamSearchMemberNodeType,
	KnowledgeCardTeamSearchRelevanceQuery,
	type KnowledgeCardTeamSearchRelevanceType,
	type KnowledgeCardTeamSearchRelevanceVariables,
} from './gql';

type ActivitySectionType = ActivityItem[];

type TeamKnowledgeCardRelevanceVariables = {
	teamQuery: string;
	orgId: string;
	siteId: string;
	activityClient: ActivityClient;
	sourceProduct?: string;
	workspaceId: string;
	traceHeaders?: TraceHeaders;
};

const NO_TEAM_MESSAGE = 'NO_TEAM';
export const noTeamResult: NLPSearchTeamResultType = {
	team_metadata: {
		id: '',
		name: '',
		avatarUrl: null,
		url: null,
		memberCount: 0,
		members: [],
		recentActivity: [],
	},
};

export const fetchTeamKnowledgeCardDataWithRanking = ({
	activityClient,
	teamQuery,
	orgId,
	siteId,
	sourceProduct,
	workspaceId,
	traceHeaders,
}: TeamKnowledgeCardRelevanceVariables): Promise<SearchAIAnswerQueryType> => {
	let teamResult: string | undefined;
	let teamData: KnowledgeCardTeamDataType | undefined;
	let activityResult: ActivitySectionType | undefined;

	const siteARI = sourceProduct ? `ari:cloud:${sourceProduct}::site/${siteId}` : '';

	const toTeam = (teamId: string) =>
		navigateToTeamsApp({
			type: 'TEAM',
			payload: {
				teamId,
			},
			cloudId: siteId,
			orgId,
		}).href;

	let actors: string[] = [];
	return fetchAggResponse<
		KnowledgeCardTeamSearchRelevanceVariables,
		KnowledgeCardTeamSearchRelevanceType
	>({
		graphQuery: KnowledgeCardTeamSearchRelevanceQuery,
		variables: {
			teamQuery,
			orgId,
			siteId: siteARI,
		},
		operationName: KNOWLEDGE_CARD_TEAM_SEARCH_RELEVANCE_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: siteId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then(({ data, errors }) => {
			const id = data?.knowledgeDiscovery.searchTeam?.id;
			if (!errors && id) {
				teamResult = id;
			} else {
				throw new Error(NO_TEAM_MESSAGE);
			}
			return fetchAggResponse<KnowledgeCardTeamDataVariables, KnowledgeCardTeamDataType>({
				graphQuery: KnowledgeCardTeamDataQuery,
				variables: {
					teamId: teamResult,
					siteId: siteId,
				},
				operationName: KNOWLEDGE_CARD_TEAM_DATA_OPERATION_NAME,
				headers: {
					...createAtlAttributionHeader({
						atlWorkspaceId: workspaceId,
						tenantId: siteId,
						product: sourceProduct ?? '',
					}),
					...(traceHeaders ?? undefined),
				},
			});
		})
		.then(({ data, errors }) => {
			if (!errors && data?.team.teamV2?.id) {
				teamData = data;
			} else {
				throw new Error(NO_TEAM_MESSAGE);
			}
			actors =
				teamData.team.teamV2?.members?.nodes
					?.map((node) => (node.member ? node.member.id : ''))
					.filter((member) => !!member) || [];
			return activityClient.fetchActivities(
				[100],
				{
					limit: 100,
					actors,
					rootContainerIds: [`ari:cloud:platform::site/${siteId}`],
				},
				['workedOn'],
				false,
			);
		})
		.then((data) => {
			if (data.workedOn && data.workedOn.length > 0) {
				activityResult = data.workedOn;
			}
			const filteredActivityResult = filterTeamActivities(activityResult || [], actors);
			const finalResult: NLPSearchTeamResultType = {
				team_metadata: {
					id: teamData?.team.teamV2?.id || '',
					name: teamData?.team.teamV2?.displayName || '',
					avatarUrl: teamData?.team.teamV2?.smallAvatarImageUrl || null,
					url: teamData?.team.teamV2?.id
						? fg('platform-adopt-teams-nav-config')
							? toTeam(teamData?.team.teamV2?.id)
							: `/people/team/${trimARI(teamData?.team.teamV2?.id)}`
						: null,
					memberCount: teamData?.team.teamV2?.members.nodes.length || 0,
					members: teamData?.team.teamV2?.members?.nodes
						? sanitizeAndMapTeamMembers(teamData.team.teamV2.members.nodes)
						: [],
					recentActivity: filteredActivityResult
						? sanitizeAndMapRecentWorkActivity(filteredActivityResult)
						: [],
				},
			};
			return {
				nlpSearch: {
					nlpResult: JSON.stringify(finalResult),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
				},
			};
		})
		.catch((error) => {
			if (error instanceof Object && 'message' in error && error.message === NO_TEAM_MESSAGE) {
				return {
					nlpSearch: {
						nlpResult: JSON.stringify(noTeamResult),
						errorState: null,
						disclaimer: null,
						format: NLPSearchResultFormat.JSON,
						uniqueSources: [],
					},
				};
			}

			if (fg('kd_enable_networkerror_logging')) {
				logger.error({
					error,
					functionName: 'fetchTeamKnowledgeCardDataWithRanking',
				});
			}
			return Promise.reject(error);
		});
};

export const fetchTeamKnowledgeCardDataWithRankingAndRequestId = async ({
	activityClient,
	teamQuery,
	orgId,
	siteId,
	sourceProduct,
	workspaceId,
	traceHeaders,
}: TeamKnowledgeCardRelevanceVariables): Promise<SearchAIAnswerQueryType> => {
	const siteARI = sourceProduct ? `ari:cloud:${sourceProduct}::site/${siteId}` : '';

	const toTeam = (teamId: string) =>
		navigateToTeamsApp({
			type: 'TEAM',
			payload: {
				teamId,
			},
			cloudId: siteId,
			orgId,
		}).href;

	return fetchAggResponse<
		KnowledgeCardTeamSearchRelevanceVariables,
		KnowledgeCardTeamSearchRelevanceType
	>({
		graphQuery: KnowledgeCardTeamSearchRelevanceQuery,
		variables: {
			teamQuery,
			orgId,
			siteId: siteARI,
		},
		operationName: KNOWLEDGE_CARD_TEAM_SEARCH_RELEVANCE_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: siteId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then(({ data, errors }) => {
			const id = data?.knowledgeDiscovery.searchTeam?.id;
			if (errors || !id) {
				throw new Error(NO_TEAM_MESSAGE);
			}
			return fetchAggResponse<KnowledgeCardTeamDataVariables, KnowledgeCardTeamDataType>({
				graphQuery: KnowledgeCardTeamDataQuery,
				variables: {
					teamId: id,
					siteId: siteId,
				},
				operationName: KNOWLEDGE_CARD_TEAM_DATA_OPERATION_NAME,
				headers: {
					...createAtlAttributionHeader({
						atlWorkspaceId: workspaceId,
						tenantId: siteId,
						product: sourceProduct ?? '',
					}),
					...(traceHeaders ?? undefined),
				},
			});
		})
		.then(async ({ data, errors, extensions }) => {
			if (errors || !data?.team.teamV2?.id) {
				throw new Error(NO_TEAM_MESSAGE);
			}
			const actors =
				data.team.teamV2?.members?.nodes
					?.map((node) => (node.member ? node.member.id : ''))
					.filter((member) => !!member) || [];

			const activityData = await activityClient.fetchActivities(
				[100],
				{
					limit: 100,
					actors,
					rootContainerIds: [`ari:cloud:platform::site/${siteId}`],
				},
				['workedOn'],
				false,
			);
			return {
				activityData,
				actors,
				extensions,
				teamData: data,
			};
		})
		.then(({ activityData, actors, extensions, teamData }) => {
			let activityResult: ActivitySectionType = [];
			if (activityData.workedOn && activityData.workedOn.length > 0) {
				activityResult = activityData.workedOn;
			}
			const filteredActivityResult = filterTeamActivities(activityResult, actors);
			const finalResult: NLPSearchTeamResultType = {
				team_metadata: {
					id: teamData?.team.teamV2?.id || '',
					name: teamData?.team.teamV2?.displayName || '',
					avatarUrl: teamData?.team.teamV2?.smallAvatarImageUrl || null,
					url: teamData?.team.teamV2?.id
						? fg('platform-adopt-teams-nav-config')
							? toTeam(teamData?.team.teamV2?.id)
							: `/people/team/${trimARI(teamData?.team.teamV2?.id)}`
						: null,
					memberCount: teamData?.team.teamV2?.members.nodes.length || 0,
					members: teamData?.team.teamV2?.members?.nodes
						? sanitizeAndMapTeamMembers(teamData.team.teamV2.members.nodes)
						: [],
					recentActivity: filteredActivityResult
						? sanitizeAndMapRecentWorkActivity(filteredActivityResult)
						: [],
				},
			};
			return {
				nlpSearch: {
					nlpResult: JSON.stringify(finalResult),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
					extraAPIAnalyticsAttributes: {
						requestId: extensions?.gateway?.request_id ?? null,
						traceId: extensions?.gateway?.trace_id ?? null,
					},
				},
			};
		})
		.catch((error) => {
			if (error instanceof Object && 'message' in error && error.message === NO_TEAM_MESSAGE) {
				return {
					nlpSearch: {
						nlpResult: JSON.stringify(noTeamResult),
						errorState: null,
						disclaimer: null,
						format: NLPSearchResultFormat.JSON,
						uniqueSources: [],
					},
				};
			}

			if (fg('kd_enable_networkerror_logging')) {
				logger.error({
					error,
					functionName: 'fetchTeamKnowledgeCardDataWithRanking',
				});
			}
			return Promise.reject(error);
		});
};

const sanitizeAndMapTeamMembers = (members: KnowledgeCardTeamSearchMemberNodeType[]) => {
	return members.reduce<NLPSearchWhoResultCollaboratorDataType[]>(function (result, memberNode) {
		if (!!memberNode.member) {
			return result.concat({
				name: memberNode.member.name || '',
				picture: memberNode.member.picture || null,
				title: memberNode.member.extendedProfile?.jobTitle || null,
				url: !!memberNode.member.id ? `/people/${trimARI(memberNode.member.id)}` : null,
				accountId: !!memberNode.member.id ? trimARI(memberNode.member.id) : null,
				department: memberNode.member.extendedProfile?.department || null,
				email: memberNode.member.email || null,
			});
		}
		return result;
	}, []);
};

const excludedEventTypes = ['commented', 'updated'];
export const filterTeamActivities = (
	activityResult: ActivityItem[],
	members: string[],
): ActivityItem[] => {
	const teamMemberSet = new Set(members);
	return activityResult.reduce<ActivityItem[]>(
		(filtered: ActivityItem[], activity: ActivityItem) => {
			const involvedTeamMembers = activity.contributors.filter((contributor) =>
				teamMemberSet.has(`ari:cloud:identity::user/${contributor.accountId}`),
			);
			if (involvedTeamMembers.length >= 1 && !excludedEventTypes.includes(activity.eventType)) {
				filtered.push({
					...activity,
					contributors: involvedTeamMembers,
				});
			}

			return filtered;
		},
		[],
	);
};
